import { useEffect, useState } from 'react'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import { useUserContext } from '@/context/userLogin'
import { Button } from '@/components/ui/shadcn/button'
import { trpc } from '@/config/trpc'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/shadcn/card'
import { useTheme } from '@/context/themeContext'
import { Moon, Sun, Monitor } from 'lucide-react'

interface FormData {
  name: string
  email: string
  company: string
}

export function AccountGeneralPage() {
  const { user } = useUserContext()
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
  })

  useEffect(() => {
    if (user) {
      setFormData({
        ...formData,
        name: user.email,
        email: user.email,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl sm:text-2xl">Personal Information</CardTitle>
          <CardDescription>Update your personal details</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="name" className="text-muted-foreground text-sm font-semibold">
              Name
            </Label>
            <div className="relative">
              <Input
                id="name"
                name={formData.name}
                value={formData.name}
                readOnly
                placeholder="Your name or company name"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-muted-foreground text-sm font-semibold">
              Email
            </Label>
            <div className="relative">
              <Input
                id="email"
                name={formData.email}
                value={formData.email}
                readOnly
                placeholder="Your name or company name"
              />
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <DeleteAccount />
        </CardFooter>
      </Card>

      <AppSettings />
    </div>
  )
}

const AppSettings = () => {
  const { theme, setTheme } = useTheme()

  const themeOptions = [
    { value: 'light' as const, label: 'Light', icon: Sun },
    { value: 'dark' as const, label: 'Dark', icon: Moon },
    { value: 'system' as const, label: 'System', icon: Monitor },
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl sm:text-2xl">App Settings</CardTitle>
        <CardDescription>Customize your app experience</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-semibold">Theme</Label>
            <div className="flex gap-2">
              {themeOptions.map((option) => {
                const Icon = option.icon
                const isActive = theme === option.value
                return (
                  <Button
                    key={option.value}
                    variant={isActive ? 'primary' : 'secondary'}
                    size="sm"
                    onClick={() => setTheme(option.value)}
                    className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {option.label}
                  </Button>
                )
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const DeleteAccount = () => {
  const { logout } = useUserContext()

  const [prompt, setPrompt] = useState(false)

  const { mutateAsync: deleteAccount } = trpc.auth.deleteAccount.useMutation()

  const handleDeleteAccount = async () => {
    setPrompt(false)
    await deleteAccount()
    logout()
  }

  return (
    <>
      <Button className="bg-red-500 hover:bg-red-600" onClick={() => setPrompt(true)}>
        Delete Account
      </Button>

      {prompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background border-border max-w-md rounded border p-4 shadow-md">
            <h2 className="text-foreground text-lg font-semibold">Delete Account</h2>
            <p className="text-muted-foreground mt-2 text-sm">
              Are you sure you want to delete your account? This action will permanently and
              immediately remove your account and all associated data.
            </p>
            <p className="text-muted-foreground mt-2 text-sm">
              <span className="font-semibold">This action cannot be undone! </span> Please make sure
              you have backed up any important data before proceeding.
            </p>
            <div className="mt-4 flex justify-end">
              <Button variant="outline" className="mr-2" onClick={() => setPrompt(false)}>
                Cancel
              </Button>
              <Button className="bg-red-500 hover:bg-red-600" onClick={handleDeleteAccount}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
